import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { cameraChanges } from '@graphistry/viz/src/client/legacy';

import styles from './styles.less';

interface KeplerProps {
    onHide: () => void;
    dataset: {
        id: string;
    };
    mode?: 'hybrid' | 'kepler' | 'graph';
}

interface LoadingModalProps {
    isLoading: boolean;
}

const LoadingModal: React.FC<LoadingModalProps> = ({ isLoading }) => (
    <Modal show={isLoading} centered backdrop="static">
        <Modal.Body className={styles['modal-body-centered']}>
            <div className={styles['modal-icon-loading']}>
                <i className="fa fa-spinner fa-spin"></i>
            </div>
            <div className={styles['modal-text']}>Loading Kepler map...</div>
        </Modal.Body>
    </Modal>
);

interface ErrorModalProps {
    error: string | null;
    onHide: () => void;
}

const ErrorModal: React.FC<ErrorModalProps> = ({ error, onHide }) => (
    <Modal show={!!error} onHide={onHide} centered>
        <Modal.Body className={styles['modal-body-centered']}>
            <div className={styles['modal-icon-error']}>
                <i className="fa fa-times-circle"></i>
            </div>
            <div className={styles['modal-error-text']}>{error}</div>
        </Modal.Body>
        <Modal.Footer>
            <button onClick={onHide} className={styles['modal-close-button']}>
                Close
            </button>
        </Modal.Footer>
    </Modal>
);

const MODE_CHANGE_EVENT = 'MODE_CHANGE';
const CAMERA_UPDATE_EVENT = 'CAMERA_UPDATE';
const KEPLER_IFRAME_HTML = `
<!DOCTYPE html>
<html>
    <head>
        <meta charset="UTF-8" />
        <title>Kepler.gl embedded map</title>

        <!--Uber Font-->
        <link
            rel="stylesheet"
            href="https://d1a3f4spazzrp4.cloudfront.net/kepler.gl/uber-fonts/4.0.0/superfine.css"
        />

        <!--MapBox css-->
        <link href="https://api.tiles.mapbox.com/mapbox-gl-js/v1.1.1/mapbox-gl.css" rel="stylesheet" />
        <link href="https://unpkg.com/maplibre-gl@^3/dist/maplibre-gl.css" rel="stylesheet" />

        <!-- Load React/Redux -->
        <script src="https://unpkg.com/react@18.3.1/umd/react.production.min.js" crossorigin></script>
        <script
            src="https://unpkg.com/react-dom@18.3.1/umd/react-dom.production.min.js"
            crossorigin
        ></script>
        <script src="https://unpkg.com/redux@4.2.1/dist/redux.js" crossorigin></script>
        <script src="https://unpkg.com/react-redux@8.1.2/dist/react-redux.min.js" crossorigin></script>
        <script
            src="https://unpkg.com/styled-components@6.1.8/dist/styled-components.min.js"
            crossorigin
        ></script>

        <!-- Load Kepler.gl-->
        <script src="https://unpkg.com/kepler.gl@3.1.0/umd/keplergl.min.js"></script>
        <!--If you want to load the build from filepath -->
        <!--<script src="../../umd/keplergl.min.js"></script>-->

        <style type="text/css">
            html, body {
                margin: 0;
                padding: 0;
                overflow: hidden;
                width: 100%;
                height: 100%;
            }
            #app {
                width: 100%;
                height: 100%;
                position: relative;
            }
        </style>
    </head>
    <body>
        <!-- We will put our React component inside this div. -->
        <div id="app">
            <!-- Kepler.gl map will be placed here-->
        </div>

        <!-- Load our React component. -->
        <script>
            /** STORE **/
            const reducers = (function createReducers(redux, keplerGl) {
                return redux.combineReducers({
                    // mount keplerGl reducer
                    keplerGl: keplerGl.keplerGlReducer
                });
            })(Redux, KeplerGl);

            const middleWares = (function createMiddlewares(keplerGl) {
                return keplerGl.enhanceReduxMiddleware([
                    // Add other middlewares here
                ]);
            })(KeplerGl);

            const enhancers = (function craeteEnhancers(redux, middles) {
                return redux.applyMiddleware(...middles);
            })(Redux, middleWares);

            const store = (function createStore(redux, enhancers) {
                const initialState = {};

                return redux.createStore(reducers, initialState, redux.compose(enhancers));
            })(Redux, enhancers);
            /** END STORE **/

            // Store config value in outer scope so it's accessible to all functions
            let AUTO_GRAPH_RENDERER_SWITCHING = true;

            // Listen for data from parent
            window.addEventListener('message', function(event) {
                if (event.data && event.data.type === 'KEPLER_DATA') {
                    const keplerDataFromBackend = JSON.parse(event.data.payload);
                    initApp(keplerDataFromBackend);
                }
            });

            window.parent.postMessage({ type: 'IFRAME_READY' }, '*');

            function initApp(keplerDataFromBackend) {
                if (!keplerDataFromBackend) return;
                const MAPBOX_TOKEN = keplerDataFromBackend?.mapboxApiAccessToken;
                AUTO_GRAPH_RENDERER_SWITCHING = keplerDataFromBackend?.config?.autoGraphRendererSwitching !== false;

                /* Validate Mapbox Token */
                // if ((MAPBOX_TOKEN || '') === '' || MAPBOX_TOKEN === 'PROVIDE_MAPBOX_TOKEN') {
                //     alert('MapBox token not configured. Set MAPBOX_API_ACCESS_TOKEN environment variable to use Kepler.gl maps.');
                // }

                /** COMPONENTS **/
                const KeplerElement = (function (react, keplerGl, mapboxToken) {
                    return function (props) {
                        const [dimensions, setDimensions] = react.useState({
                            width: window.innerWidth,
                            height: window.innerHeight
                        });

                        react.useEffect(() => {
                            const handleResize = () => {
                                setDimensions({
                                    width: window.innerWidth,
                                    height: window.innerHeight
                                });
                            };

                            window.addEventListener('resize', handleResize);
                            handleResize(); // Call immediately to get initial size

                            return () => window.removeEventListener('resize', handleResize);
                        }, []);

                        return react.createElement(
                            'div',
                            {style: {position: 'absolute', left: 0, top: 0, width: '100%', height: '100%'}},
                            react.createElement(keplerGl.KeplerGl, {
                                mapboxApiAccessToken: mapboxToken,
                                id: 'map',
                                width: dimensions.width,
                                height: dimensions.height
                            })
                        );
                    };
                })(React, KeplerGl, MAPBOX_TOKEN);

                /** Initialize app and load data **/
                // Process CSV data in each dataset using Kepler's processor
                keplerDataFromBackend.datasets.forEach(dataset => {
                    if (dataset.data && typeof dataset.data === 'string') {
                        dataset.data = KeplerGl.processCsvData(dataset.data);
                    }
                });

                const data = keplerDataFromBackend.datasets;

                // Create the app
                const app = (function createReactReduxProvider(react, reactRedux, KeplerElement) {
                    return react.createElement(
                        reactRedux.Provider,
                        {store},
                        react.createElement(KeplerElement, null)
                    );
                })(React, ReactRedux, KeplerElement);

                // Render the app
                const container = document.getElementById('app');
                const root = ReactDOM.createRoot(container);
                root.render(app);

                // If we have data, dispatch it to the store after rendering
                // Wait for Kepler.gl to mount and initialize its reducer
                if (data) {
                    // Single subscription to handle both Kepler init and data loading
                    let previousState = store.getState();
                    let hasDispatchedData = false;
                    let hasNotifiedReady = false;

                    const unsubscribe = store.subscribe(() => {
                        const state = store.getState();
                        const keplerState = state.keplerGl?.map;

                        // Stage 1: Wait for Kepler reducer to initialize
                        if (!hasDispatchedData && keplerState !== undefined) {
                            hasDispatchedData = true;

                            // Prepare datasets
                            const datasets = data;
                            const config = keplerDataFromBackend.layers ? {
                                version: 'v1',
                                config: {
                                    visState: {
                                        layers: keplerDataFromBackend.layers,
                                        layerBlending: keplerDataFromBackend.config?.overlayBlending || 'normal'
                                    },
                                    ...(keplerDataFromBackend.config?.tileStyle && {
                                        mapStyle: keplerDataFromBackend.config.tileStyle
                                    })
                                }
                            } : undefined;

                            const options = keplerDataFromBackend.options || {
                                centerMap: true,
                                readOnly: false
                            };

                            // Dispatch data to Kepler
                            store.dispatch(KeplerGl.addDataToMap({ datasets, config, options }));
                        }

                        // Stage 2: Wait for data to load and viewport to initialize
                        if (hasDispatchedData && !hasNotifiedReady) {
                            const prevKeplerState = previousState.keplerGl?.map;

                            const datasetsLoaded = keplerState?.visState?.datasets &&
                                                    Object.keys(keplerState.visState.datasets).length > 0;
                            const viewportInitialized = keplerState?.mapState?.latitude !== undefined &&
                                                        keplerState?.mapState?.longitude !== undefined;
                            const stateChanged = !prevKeplerState?.visState?.datasets ||
                                                Object.keys(prevKeplerState.visState.datasets).length === 0;

                            if (datasetsLoaded && viewportInitialized && stateChanged) {
                                hasNotifiedReady = true;

                                // Close side panel and notify parent
                                store.dispatch(KeplerGl.toggleSidePanel());
                                window.parent.postMessage({ type: 'KEPLER_READY' }, '*');

                                // Done, unsubscribe
                                unsubscribe();
                            }
                        }

                        previousState = state;
                    });
                }
            }
        </script>

        <script>
            /**
             * Customize map.
             * Interact with map store to customize data and behavior
             */
            (function customize(keplerGl, store) {
                // Modified Earth circumference (scaled down by 10000x for manageable numbers)
                const EARTH_CIRCUMFERENCE = 4007.5016686; // ~4,007 instead of 40,075,016
                const R = EARTH_CIRCUMFERENCE / (2 * Math.PI);

                // Convert Mercator coordinates to latitude/longitude
                function mercatorToLatLon(x, y) {
                    // Convert from Mercator projection back to lat/lon
                    const lon = (x / R) * (180 / Math.PI);
                    const lat = (2 * Math.atan(Math.exp(y / R)) - Math.PI / 2) * (180 / Math.PI);

                    return { latitude: lat, longitude: lon };
                }

                // Calculate zoom level from viewport width
                function calculateZoomFromViewport(widthMeters, canvasWidth) {
                    // Default canvas width if not provided
                    const width = canvasWidth || window.innerWidth || 1920;

                    // Calculate meters per pixel from viewport width
                    const metersPerPixel = widthMeters / width;

                    // Calculate world width in pixels at this scale
                    const worldWidthPixels = EARTH_CIRCUMFERENCE / metersPerPixel;

                    // Calculate zoom level (256 pixels = tile size at zoom 0)
                    // Subtract 1 as an empirical adjustment to align zoom levels between systems
                    const zoomLevel = Math.log2(worldWidthPixels / 256) - 1;

                    // Clamp between 0 and 20
                    return Math.max(0, Math.min(20, zoomLevel));
                }

                // Listen for camera updates and mode changes from parent window
                window.addEventListener('message', function(event) {
                    if (event.data && event.data.type === '${CAMERA_UPDATE_EVENT}') {
                        const { center, width } = event.data.payload;

                        // Convert Mercator coordinates to lat/lon
                        const { latitude, longitude } = mercatorToLatLon(center.x, center.y);

                        // Calculate zoom level from viewport width
                        const keplerZoom = calculateZoomFromViewport(width, window.innerWidth);

                        // Dispatch Kepler action to update the map view
                        store.dispatch(keplerGl.updateMap({
                            latitude: -latitude,
                            longitude: longitude,
                            zoom: keplerZoom
                        }));
                    }

                    if (event.data && event.data.type === '${MODE_CHANGE_EVENT}' && AUTO_GRAPH_RENDERER_SWITCHING) {
                        const { mode } = event.data.payload;
                        const state = store.getState();
                        const keplerState = state.keplerGl?.map;

                        if (!keplerState || !keplerState.visState || !keplerState.visState.layers) {
                            return;
                        }

                        // Toggle layer visibility based on mode
                        const shouldShowGraphLayers = mode === 'kepler';

                        // Only toggle visibility for point, arc, and line layers (nodes and edges)
                        const nodeEdgeLayerTypes = new Set(['point', 'arc', 'line']);

                        keplerState.visState.layers.forEach(layer => {
                            if (layer && layer.id && nodeEdgeLayerTypes.has(layer.type)) {
                                store.dispatch(keplerGl.layerConfigChange(layer, {
                                    ...layer.config,
                                    isVisible: shouldShowGraphLayers
                                }));
                            }
                        });
                    }
                });

                // Additional customizations can be added here
                // For example: store.dispatch(keplerGl.toggleSplitMap());
            })(KeplerGl, store);
        </script>
    </body>
</html>`;

export const Kepler: React.FC<KeplerProps> = ({ onHide, dataset, mode = 'graph' }) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    // Hide Kepler controls UI when not in "kepler" mode and toggle layer visibility
    useEffect(() => {
        const iframeDoc = iframeRef.current?.contentWindow?.document;
        if (!iframeDoc || !iframeDoc.head) return;

        let style = iframeDoc.getElementById('kepler-mode-style') as HTMLStyleElement;
        if (!style) {
            style = iframeDoc.createElement('style');
            style.id = 'kepler-mode-style';
            iframeDoc.head.appendChild(style);
        }

        style.textContent = mode !== 'kepler'
            ? '.side-panel--container { display: none !important; } .map-control { display: none !important; }'
            : '';

        if (iframeRef.current && iframeRef.current.contentWindow) {
            iframeRef.current.contentWindow.postMessage({
                type: MODE_CHANGE_EVENT,
                payload: { mode }
            }, '*');
        }
    }, [mode]);

    // Initialize Kepler
    useEffect(() => {
        setIsLoading(true);
        setError(null);
        let messageHandler: ((event: MessageEvent) => void) | null = null;

        const sendCameraUpdate = (camera: {
            center: { x: number; y: number };
            width: number;
        }) => {
            if (iframeRef.current && iframeRef.current.contentWindow) {
                iframeRef.current.contentWindow.postMessage({
                    type: CAMERA_UPDATE_EVENT,
                    payload: {
                        center: camera.center,
                        width: camera.width
                    }
                }, '*');
            }
        }

        const keplerData = fetch(`/api/v2/etl/datasets/${dataset?.id}/kepler/`, {
            method: 'GET',
            cache: 'force-cache',  // Prefer cached response if available
            credentials: 'same-origin'
        })
            .then(async response => {
                if (!response.ok) {
                    let errorMessage = 'Failed to load Kepler configuration';
                    try {
                        const errorData = await response.json();
                        if (errorData.message) {
                            errorMessage = errorData.message;
                        } else if (errorData.error) {
                            errorMessage = errorData.error;
                        }
                    } catch (parseError) {
                        errorMessage = `Server returned status ${response.status}`;
                    }
                    setError(errorMessage);
                    setIsLoading(false);
                    throw new Error(errorMessage);
                }
                return response.text();
            })
            .catch(error => {
                console.error('Error fetching Kepler data:', error);
                if (!error.message || error.message === 'Failed to fetch') {
                    setError('Network error: Unable to connect to server');
                }
                setIsLoading(false);
            });

        const handleIframeMessages = async (event: MessageEvent) => {
            if (event.source !== iframeRef.current?.contentWindow) return;

            if (event.data?.type === 'IFRAME_READY') {
                iframeRef.current?.contentWindow?.postMessage({
                    type: 'KEPLER_DATA',
                    payload: await keplerData
                }, '*');
            }

            if (event.data?.type === 'KEPLER_READY') {
                setIsLoading(false);
                cameraChanges.take(1).subscribe(camera => {
                    // TODO: two consecutive camera updates are sent to deal with KeplerGL camera state issues
                    // remove when root issue is fixed in KeplerGL
                    sendCameraUpdate(camera);
                    setTimeout(() => sendCameraUpdate(camera), 100);
                });
                // Trigger mode change on init to apply correct layer settings
                iframeRef.current.contentWindow.postMessage({
                    type: MODE_CHANGE_EVENT,
                    payload: { mode }
                }, '*');
            }
        };

        messageHandler = handleIframeMessages;
        window.addEventListener('message', messageHandler);

        // Subscribe to camera changes from Graphistry renderer
        const subscription = cameraChanges.subscribe(sendCameraUpdate);

        return () => {
            if (messageHandler) {
                window.removeEventListener('message', messageHandler);
            }
            subscription.unsubscribe();
        };
    }, [dataset.id]);

    return (
        <>
            <LoadingModal isLoading={isLoading} />
            <ErrorModal error={error} onHide={onHide} />
            {!error && (
                <div
                    className={`kepler ${styles['kepler-container']}`}
                    style={{ zIndex: mode === 'kepler' ? 10000 : -1 }}
                >
                    <div className={styles['kepler-content']}>
                        <iframe
                            ref={iframeRef}
                            srcDoc={KEPLER_IFRAME_HTML}
                            title="Kepler Map"
                            className={styles['kepler-iframe']}
                        />
                    </div>
                </div>
            )}
        </>
    );
};
